import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Flex, useDisclosure } from '@chakra-ui/react';
import { FormattedMessage } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { Blocks } from '../../components/sfcc-content-rendering/Blocks';
import { HtmlSnippet } from '../../components/sfcc-content-rendering/HtmlSnippet';
import { ProductsAddedToBagDrawer } from '../../contexts/products-added-to-bag-drawer/ProductsAddedToBagDrawer';
import { useGTM } from '../../hooks';
import { Breadcrumb, Header, Text } from '../../ui-components';
import { useBreadcrumbs } from '../../ui-components/breadcrumbs/useBreadcrumbs';
import { getProductType } from '../../utils/product-type-utils';
import { BreadcrumbSchema } from '../../utils/schema';
import { getOriginUrl } from '../../utils/url-utils';
import { SearchHead } from './SearchHead';
import { FilterDrawer } from './components/filters/FilterDrawer';
import { FilterButtons } from './components/filters/filters-buttons/FilterButtons';
import { CategorySearchNoResult } from './components/no-results/CategorySearchNoResult';
import { RecipeCategoryHeader } from './components/recipe-category-header';
import { SearchListing } from './components/search-listing/SearchListing';
import { BreadcrumbSkeleton } from './components/skeletons/BreadcrumbSkeleton';
import { SearchSkeletons } from './components/skeletons/SearchSkeletons';
import { BloomreachProductType } from './hooks/use-search';
import { useSearch } from './hooks/use-search/useSearch';
import {
    cleanPathname,
    convertSearchParamsToURLParams,
    convertURLParamsToSearchParams,
} from './utils/search-url-params-utils';
import type { Block } from '../../components/sfcc-content-rendering/Blocks';
import type { CategoryInfo } from '../show/api/getContentData';

interface CategorySearchProps {
    categoryInfo?: CategoryInfo;
}

const recipeTopSlots = ['recipe-listing-slot-2', 'recipe-listing-slot-3', 'recipe-listing-slot-4'];

const recipeBottomSlots = ['recipe-listing-slot-5', 'recipe-seo-bottom-block'];

const productTopSlots = [
    'product-listing-slot-3',
    'product-listing-slot-1',
    'product-listing-slot-2',
    'product-listing-slot-4',
];

const CategorySearch: FC<CategorySearchProps> = ({ categoryInfo }) => {
    const category = categoryInfo?.response?.category;

    const pagePlacementBlocks = useMemo<Map<number, Block[]>>(() => {
        const c_productInfoTile: string[] | undefined = category?.c_productInfoTile;
        const contentAssets: Record<string, Block[]> =
            categoryInfo?.response?.categoryContent?.contentAssets ?? {};
        const productInfoTile = c_productInfoTile
            ?.map((x) => x.split(','))
            ?.map((r): [number, string] => [parseInt(r[0], 10), String(r[1]).trim()]);
        const entries: [number, Block[]][] =
            productInfoTile?.map(([position, contentAssetId]) => [
                position,
                contentAssets[contentAssetId] ?? [],
            ]) ?? [];
        return new Map<number, Block[]>(entries.filter(([_, blocks]) => blocks.length > 0));
    }, [category?.c_productInfoTile, categoryInfo?.response?.categoryContent?.contentAssets]);

    const location = useLocation();
    const { pathname, search: searchLocation } = location;

    const productType = useMemo(
        () => getProductType(pathname, searchLocation) as BloomreachProductType,
        [pathname, searchLocation],
    );

    const search = useSearch(productType, cleanPathname(location.pathname));

    const { updatePageData, onPlpPageView } = useGTM();

    useEffect(() => {
        if (category) {
            onPlpPageView(category);
        }
    }, [category, onPlpPageView]);

    useEffect(() => {
        updatePageData({
            search: {
                products: search.items,
                params: search.searchParams,
            },
        });
    }, [updatePageData, search.searchParams, search.items]);

    const { isOpen, onOpen, onClose } = useDisclosure();
    const [expandedFilters, setExpandedFilters] = useState<number[]>([]);
    const [scrollToFilterIndex, setScrollToFilterIndex] = useState<number | null>(null);

    const { hasHardgoodTopSlots, hasRecipeTopSlots } = useMemo(() => {
        const categorySlots = categoryInfo?.response?.categoryContent?.categorySlots ?? {};

        const hasHardgoodSlots =
            productType === BloomreachProductType.hardgood &&
            productTopSlots.some((slotId) => (categorySlots[slotId] ?? []).length > 0);

        const hasRecipeSlots =
            productType === BloomreachProductType.recipe &&
            recipeTopSlots.some((slotId) => (categorySlots[slotId] ?? []).length > 0);

        return {
            hasHardgoodTopSlots: hasHardgoodSlots,
            hasRecipeTopSlots: hasRecipeSlots,
        };
    }, [categoryInfo?.response?.categoryContent?.categorySlots, productType]);

    const { hasHardgoodBottomSlots, hasRecipeBottomSlots } = useMemo(() => {
        const categorySlots = categoryInfo?.response?.categoryContent?.categorySlots ?? {};
        const globalSlots = categoryInfo?.response?.categoryContent?.globalSlots ?? {};
        const contentAssets = categoryInfo?.response?.categoryContent?.contentAssets ?? {};

        const hasHardgoodSlots =
            productType === BloomreachProductType.hardgood &&
            ((): boolean => {
                const slot5 = globalSlots['product-listing-slot-5'] ?? [];
                const slot6 = categorySlots['product-listing-slot-6'] ?? [];
                const bottomBlock = categorySlots['cat-landing-bottom-block'] ?? [];
                const seoContentBlock = contentAssets['seo-content-block-js-css'] ?? [];
                const seoHtml = category?.c_seo ?? '';
                return (
                    slot5.length > 0 ||
                    slot6.length > 0 ||
                    bottomBlock.length > 0 ||
                    seoContentBlock.length > 0 ||
                    seoHtml.trim().length > 0
                );
            })();

        const hasRecipeSlots =
            productType === BloomreachProductType.recipe &&
            recipeBottomSlots.some((slotId) => (categorySlots[slotId] ?? []).length > 0);

        return {
            hasHardgoodBottomSlots: hasHardgoodSlots,
            hasRecipeBottomSlots: hasRecipeSlots,
        };
    }, [
        categoryInfo?.response?.categoryContent?.categorySlots,
        categoryInfo?.response?.categoryContent?.globalSlots,
        categoryInfo?.response?.categoryContent?.contentAssets,
        category?.c_seo,
        productType,
    ]);

    const handleExpandedFiltersChange = useCallback((indices: number | number[]) => {
        if (Array.isArray(indices)) {
            setExpandedFilters(indices);
        }
    }, []);

    const onCloseFilterDrawer = useCallback(() => {
        onClose();
        setScrollToFilterIndex(null);
    }, [onClose]);

    const onOpenFilterDrawer = useCallback(
        (filterIndex: number) => {
            // -1 means "don't auto-open any filter" (used when clicking All Filters with active selections)
            if (
                filterIndex !== undefined &&
                filterIndex >= 0 &&
                !expandedFilters.includes(filterIndex)
            ) {
                setExpandedFilters((prev) => [...prev, filterIndex]);
            }
            setScrollToFilterIndex(filterIndex >= 0 ? filterIndex : null);
            onOpen();
        },
        [onOpen, expandedFilters],
    );

    const breadcrumbs = useBreadcrumbs(categoryInfo?.response?.category?.parentCategoryTree);

    const canonicalUrl = useMemo(() => {
        const urlParams = new URLSearchParams(location.search);
        const searchParamsFromUrl = convertURLParamsToSearchParams({
            urlParams,
            productType,
        });
        const params = convertSearchParamsToURLParams({
            productType: searchParamsFromUrl.productType,
            page: searchParamsFromUrl.page,
        }).toString();
        return `${getOriginUrl()}${location.pathname}${params ? `?${params}` : ''}`;
    }, [location.search, location.pathname, productType]);

    const prevUrl = useMemo(() => {
        if (!canonicalUrl) {
            return undefined;
        }
        const urlParams = new URL(canonicalUrl).searchParams;
        const pageStr = urlParams.get('page');
        urlParams.delete('page');
        const page = pageStr ? parseInt(pageStr, 10) : 1;
        if (page <= 1) {
            // we are on the first page
            return undefined;
        }
        if (page === 2) {
            // we are on the second page, so we don't need to add the page parameter
            return `${getOriginUrl()}${location.pathname}`;
        }
        urlParams.set('page', (page - 1).toString());

        return `${getOriginUrl()}${location.pathname}?${urlParams.toString()}`;
    }, [canonicalUrl, location.pathname]);

    const nextUrl = useMemo(() => {
        if (!canonicalUrl) {
            return undefined;
        }
        if (!search.hasNextPage) {
            return undefined;
        }
        const urlParams = new URL(canonicalUrl).searchParams;
        const pageStr = urlParams.get('page');
        const page = pageStr ? parseInt(pageStr, 10) : 1;
        urlParams.set('page', (page + 1).toString());
        return `${getOriginUrl()}${location.pathname}?${urlParams.toString()}`;
    }, [canonicalUrl, location.pathname, search.hasNextPage]);

    const showSkeleton = search.isLoading;
    if (showSkeleton) {
        return <SearchSkeletons productType={productType} />;
    }

    return (
        <div className="h-full w-full" style={{ overflowX: 'clip' }}>
            <div className="mx-auto h-full w-full max-w-[1928px]">
                <SearchHead canonicalUrl={canonicalUrl} prevUrl={prevUrl} nextUrl={nextUrl} />
                <div className="flex flex-col gap-4 px-0 pb-4 sm:px-8 sm:py-4 lg:px-10 xl:px-16">
                    <div className="order-last justify-start px-4 sm:px-0 md:order-first md:flex">
                        {/* Use == null to handle both null and undefined consistently - shows skeleton when categoryInfo is not available */}
                        {categoryInfo == null ? (
                            <BreadcrumbSkeleton />
                        ) : (
                            breadcrumbs.length > 0 && (
                                <>
                                    <Breadcrumb itemsList={breadcrumbs} />
                                    <BreadcrumbSchema breadcrumbTrail={breadcrumbs} />
                                </>
                            )
                        )}
                    </div>
                    <Flex direction="column" gap={6} data-testid="category-search-slot-header">
                        {hasHardgoodTopSlots && (
                            <Flex direction="column" gap={6} paddingX={{ base: 4, sm: 0 }}>
                                {productTopSlots.map((slotId) => (
                                    <Blocks
                                        key={slotId}
                                        contentBlocks={
                                            categoryInfo?.response?.categoryContent?.categorySlots
                                        }
                                        id={slotId}
                                    />
                                ))}
                            </Flex>
                        )}
                        <Flex flexDirection="column" gap={4}>
                            {productType === BloomreachProductType.recipe ? (
                                <RecipeCategoryHeader
                                    categoryName={categoryInfo?.response?.category?.name}
                                    categorySlots={
                                        categoryInfo?.response?.categoryContent?.globalSlots
                                    }
                                    currentPathname={pathname}
                                />
                            ) : null}
                            {hasRecipeTopSlots && (
                                <Flex direction="column" gap={6} paddingX={{ base: 4, sm: 0 }}>
                                    {recipeTopSlots.map((slotId) => (
                                        <Blocks
                                            key={slotId}
                                            contentBlocks={
                                                categoryInfo?.response?.categoryContent
                                                    ?.categorySlots
                                            }
                                            id={slotId}
                                        />
                                    ))}
                                </Flex>
                            )}
                            {productType === BloomreachProductType.hardgood ? (
                                <Flex
                                    justifyContent="space-between"
                                    alignItems="center"
                                    paddingX={{ base: 4, sm: 0 }}
                                >
                                    <Header
                                        id="search.results.for.category"
                                        color="slt-pepper.1000"
                                        as="h1"
                                        variant="header4"
                                        style="bold"
                                    >
                                        {categoryInfo?.response?.category?.name}
                                    </Header>
                                    <Text data-testid="category-product-count">
                                        <FormattedMessage
                                            id="product.count"
                                            defaultMessage="{count, plural, =0 {0 Items} one {# Item} other {# Items}}"
                                            values={{ count: Math.max(0, search.total) }}
                                        />
                                    </Text>
                                </Flex>
                            ) : null}

                            <FilterButtons
                                search={search}
                                onOpenFilterDrawer={onOpenFilterDrawer}
                            />

                            {search.total === 0 ? (
                                <CategorySearchNoResult />
                            ) : (
                                <SearchListing
                                    id="category-search"
                                    search={search}
                                    pagePlacementBlocks={pagePlacementBlocks}
                                    source={
                                        productType === BloomreachProductType.hardgood
                                            ? 'PLP'
                                            : 'RLP'
                                    }
                                />
                            )}
                        </Flex>

                        {hasHardgoodBottomSlots && (
                            <Flex direction="column" gap={6} paddingX={{ base: 4, sm: 0 }}>
                                <Blocks
                                    id="product-listing-slot-5"
                                    contentBlocks={
                                        categoryInfo?.response?.categoryContent?.globalSlots
                                    }
                                />
                                <Blocks
                                    id="product-listing-slot-6"
                                    contentBlocks={
                                        categoryInfo?.response?.categoryContent?.categorySlots
                                    }
                                />
                                <Blocks
                                    id="cat-landing-bottom-block"
                                    contentBlocks={
                                        categoryInfo?.response?.categoryContent?.categorySlots
                                    }
                                />
                                <Blocks
                                    id="seo-content-block-js-css"
                                    contentBlocks={
                                        categoryInfo?.response?.categoryContent?.contentAssets
                                    }
                                    className="hidden"
                                />
                                <HtmlSnippet
                                    id="seo-content"
                                    html={categoryInfo?.response?.category?.c_seo ?? ''}
                                />
                            </Flex>
                        )}

                        {hasRecipeBottomSlots && (
                            <Flex direction="column" gap={6} paddingX={{ base: 4, sm: 0 }}>
                                {recipeBottomSlots.map((slotId) => (
                                    <Blocks
                                        key={slotId}
                                        contentBlocks={
                                            categoryInfo?.response?.categoryContent?.categorySlots
                                        }
                                        id={slotId}
                                    />
                                ))}
                            </Flex>
                        )}
                    </Flex>
                </div>
                <FilterDrawer
                    search={search}
                    isOpen={isOpen}
                    onClose={onCloseFilterDrawer}
                    expandedFilters={expandedFilters}
                    onExpandedFiltersChange={handleExpandedFiltersChange}
                    scrollToFilterIndex={scrollToFilterIndex}
                />
                <ProductsAddedToBagDrawer />
            </div>
        </div>
    );
};

export default CategorySearch;
